import React, { useMemo, useState } from 'react';
import {
  Card,
  Descriptions,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  DatePicker,
  Upload,
  message,
  Alert,
  Spin,
  Select,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  FileOutlined,
  CameraOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useIIRByWorkOrder,
  useCreateIIR,
  useApproveIIR,
  useRejectIIR,
  useAssignInspector,
} from '../../hooks/useQCWorkflow';
import { useUsers } from '../../hooks/useUsers';
import { InitialInspectionRequest } from '../../services/qc-workflow.service';
import { buildUserIdToLabelMap, resolveUserDisplayFromMap } from '@/utils/userDisplay';

const { TextArea } = Input;

interface InitialInspectionRequestProps {
  workOrderId: string;
}

const InitialInspectionRequestComponent: React.FC<InitialInspectionRequestProps> = ({ workOrderId }) => {
  const [form] = Form.useForm();
  const [assignForm] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);

  const { data: iir, isLoading, refetch } = useIIRByWorkOrder(workOrderId);
  const { data: users = [], isLoading: isLoadingUsers } = useUsers({ is_active: true });
  const userDisplayMap = useMemo(() => buildUserIdToLabelMap(users), [users]);
  const ru = (id?: string | null) => resolveUserDisplayFromMap(userDisplayMap, id, '—');
  const createMutation = useCreateIIR();
  const assignMutation = useAssignInspector();
  const approveMutation = useApproveIIR();
  const rejectMutation = useRejectIIR();

  const handleCreate = async () => {
    try {
      await createMutation.mutateAsync(workOrderId);
      refetch();
    } catch (error) {
      console.error('Error creating IIR:', error);
    }
  };

  const handleApprove = async () => {
    if (!iir) return;
    try {
      await approveMutation.mutateAsync(iir.id);
      refetch();
    } catch (error) {
      console.error('Error approving IIR:', error);
    }
  };

  const handleAssignInspector = async (values: { inspectorId: string }) => {
    if (!iir) return;
    try {
      await assignMutation.mutateAsync({
        id: iir.id,
        inspectorId: values.inspectorId,
      });
      setIsAssignModalVisible(false);
      assignForm.resetFields();
      refetch();
    } catch (error) {
      console.error('Error assigning inspector:', error);
    }
  };

  const handleReject = async (values: { rejectionReason: string }) => {
    if (!iir) return;
    try {
      await rejectMutation.mutateAsync({
        id: iir.id,
        rejectionReason: values.rejectionReason,
      });
      setIsRejectModalVisible(false);
      rejectForm.resetFields();
      refetch();
    } catch (error) {
      console.error('Error rejecting IIR:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Pending: 'default',
      'In Progress': 'processing',
      Approved: 'success',
      Rejected: 'error',
      Cancelled: 'default',
    };
    return colors[status] || 'default';
  };

  if (isLoading) {
    return <Spin />;
  }

  if (!iir) {
    return (
      <Card>
        <Alert
          message="No Job Inspection"
          description="Job Inspection must be created and approved before operations can begin on this work order."
          type="warning"
          showIcon
          action={
            <Button
              type="primary"
              onClick={handleCreate}
              loading={createMutation.isPending}
            >
              Create Job Inspection
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card
        title={
          <Space>
            <span>Job Inspection</span>
            <Tag color={getStatusColor(iir.status)}>{iir.status}</Tag>
          </Space>
        }
        extra={
          <Space>
            {iir.status === 'Pending' && (
              <Button
                type="primary"
                icon={<UserOutlined />}
                onClick={() => setIsAssignModalVisible(true)}
              >
                Assign Inspector
              </Button>
            )}
            {iir.status === 'In Progress' && (
              <>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={handleApprove}
                  loading={approveMutation.isPending}
                >
                  Approve
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => setIsRejectModalVisible(true)}
                >
                  Reject
                </Button>
              </>
            )}
          </Space>
        }
      >
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Job Inspection Number">{iir.iir_number}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getStatusColor(iir.status)}>{iir.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Requested Date">
            {new Date(iir.requested_date).toLocaleDateString()}
          </Descriptions.Item>
          <Descriptions.Item label="Requested By">
            {iir.requester?.name || ru(iir.requested_by)}
          </Descriptions.Item>
          {iir.inspector_id && (
            <>
              <Descriptions.Item label="Inspector">
                {iir.inspector?.name || ru(iir.inspector_id)}
              </Descriptions.Item>
              <Descriptions.Item label="Inspection Date">
                {iir.inspection_date ? new Date(iir.inspection_date).toLocaleDateString() : '-'}
              </Descriptions.Item>
            </>
          )}
          {iir.approved_date && (
            <>
              <Descriptions.Item label="Approved Date">
                {new Date(iir.approved_date).toLocaleDateString()}
              </Descriptions.Item>
              <Descriptions.Item label="Approved By">
                {iir.approver?.name || ru(iir.approved_by)}
              </Descriptions.Item>
            </>
          )}
          {iir.rejection_reason && (
            <Descriptions.Item label="Rejection Reason" span={2}>
              {iir.rejection_reason}
            </Descriptions.Item>
          )}
          {iir.findings && (
            <Descriptions.Item label="Findings" span={2}>
              {iir.findings}
            </Descriptions.Item>
          )}
          {iir.observations && (
            <Descriptions.Item label="Observations" span={2}>
              {iir.observations}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {iir.status === 'Approved' && (
        <Alert
          message="Job Inspection Approved"
          description="Operations can now proceed on this work order."
          type="success"
          showIcon
        />
      )}

      {iir.status === 'Rejected' && (
        <Alert
          message="Job Inspection Rejected"
          description="This work order cannot proceed until a new Job Inspection is created and approved."
          type="error"
          showIcon
        />
      )}

      <Modal
        title="Assign Inspector"
        open={isAssignModalVisible}
        onOk={() => assignForm.submit()}
        onCancel={() => {
          setIsAssignModalVisible(false);
          assignForm.resetFields();
        }}
        confirmLoading={assignMutation.isPending}
      >
        <Form
          form={assignForm}
          layout="vertical"
          onFinish={handleAssignInspector}
        >
          <Form.Item
            name="inspectorId"
            label="Select Inspector"
            rules={[{ required: true, message: 'Please select an inspector' }]}
          >
            <Select
              placeholder="Select an inspector"
              loading={isLoadingUsers}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={users.map(user => ({
                value: user.id,
                label: `${user.first_name} ${user.last_name || ''} (${user.email})`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Reject Job Inspection"
        open={isRejectModalVisible}
        onOk={() => rejectForm.submit()}
        onCancel={() => {
          setIsRejectModalVisible(false);
          rejectForm.resetFields();
        }}
        confirmLoading={rejectMutation.isPending}
      >
        <Form
          form={rejectForm}
          layout="vertical"
          onFinish={handleReject}
        >
          <Form.Item
            name="rejectionReason"
            label="Rejection Reason"
            rules={[{ required: true, message: 'Please enter rejection reason' }]}
          >
            <TextArea
              rows={4}
              placeholder="Enter reason for rejection"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default InitialInspectionRequestComponent;
